import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import UseComsumeOrder from '../../modules/consume/presentation/UseConsumeOrder';
import UseExtract from '../../modules/consume/presentation/UseExtract'
import UseOrderPad from '../../modules/consume/presentation/UseOrderPad'
import { useUi } from '../../modules/ui/presentation/context/UiContext';
import { v4 } from 'uuid'
import { SystemType } from '../../modules/device/domain/enums/SystemType';
import { useCartStore } from '../../modules/cart/infra/store/CartStore';
import { PaymentType } from '../../modules/consume/domain/models/PaymentType';
import { useI18n } from '../../hooks/useI18n';
import { queryParamsJSON } from '../../cross-cutting/queryParamsJSON';
import UseAccount from '../../modules/consume/presentation/UseAccount';
import UsePrePaidTickets from '../../modules/prePaidTickets/presentation/UsePrePaidTickets';
import useOperatorStore from '../../modules/operator/store/OperatorStore';
import UseDeviceStore from '../../modules/device/store/DeviceStore';
import { OperationMode } from '../../modules/device/presentation/context/OperationMode';
import { ICancelOrderItemSelectable } from './components/cancelOrderForm/CancelOrderForm';
import { OperatorMode } from '../../modules/device/interface/Device';
import { useSession } from '../../modules/session/presentation/hooks/UseSession';
import { hideLoading, showLoading } from '../../modules/ui/stores/UiStore';
import OnBoardingService from '../../services/api/onBoarding/OnBoardingService';
import { OnBoardingSteps } from '../../modules/onBoarding/domain/enums/OnBoardingSteps';
import UseOnboardingStore from '../../modules/onBoarding/store/OnBoardingStore';
import UseInvoice from '../../modules/invoice/presentation/hooks/UseInvoice';
import UsePaymentCieloPos from '../../modules/PosCielo/presentation/hook/UsePaymentCieloPos';
import ISimplePos from '../../modules/simplePos/model/ISimplePos';
import CodePanelApi from '../../services/api/consume/CodePanelApi';
import { GetCodePanelUseCase } from '../../modules/consume/application/useCases/codePanel/GetCodePanelUseCase';
import { ICustomerFormValue } from '../TableMap/components/customerForm/ICustomerFormValue';
import UsePrinter from '../../modules/printer/presentation/hooks/UsePrinter';
import { IDiscountValue } from './components/discount/Discount';
import { ITransferItemSelectable } from './components/transferItemsForm/TransferItemsForm';
import { SyncOrderTransactions } from '../../modules/prePaidTickets/domain/dtos/SyncPrePaidTickets';

const codePanelService = CodePanelApi();


const UseCatalogPage = (ticket?: boolean) => {

    const { t } = useI18n();

    const [device, operationMode, deviceConfig, isSystemType, disabledSound] = UseDeviceStore(state => [state.device, state.operationMode, state.deviceConfig, state.isSystemType, state.disbledSound, state.receiptPrinterSector]);

    const { getOrderPad, orderPad } = UseOrderPad();
    const { getExtract, extract } = UseExtract();
    const { closeAccount, payAccount, cancelOrderItems, transferOrderItems } = UseAccount();
    const { consumeOrder } = UseComsumeOrder();
    const { logoutSessionHandler, session } = useSession();
    const [onFinishStep] = UseOnboardingStore(state => [state.onFinishStep]);
    const { consumeTicketOrder } = UsePrePaidTickets();
    const { emitBackgroundInvoice } = UseInvoice();
    const { paymentCieloPos } = UsePaymentCieloPos();
    const [cartItems, clearCart, totals] = useCartStore(state => [state.cartItems, state.clearCart, state.totals]);

    const navigate = useNavigate();

    const [onLoadingExtract, setOnLoadingExtract] = useState(true)
    const { tag, tableId, number } = useParams();

    const [currentOperator] = useOperatorStore((state) => ([state.currentOperator]));
    const { fullScreenToast, toast } = useUi();
    const { printTicketOrder, printExtract, receiptPrinterSector, printPosPayment } = UsePrinter();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [searchParams, setSearchParms] = useSearchParams();

    const printExtractHandler = useCallback(async () => {
        if (extract) {
            printExtract(extract, number ?? tag ?? tableId ?? '');
        }
    }, [extract, number, printExtract, tableId, tag])

    useEffect(() => {
        const isPos = isSystemType(SystemType.pospayOrderPad);
        const isPrepay = isSystemType(SystemType.prepayTicket);
        // return !isPos && !isPrepay;
        if (!isPos && !isPrepay) {
            toast("Nenhuma operaçao detectada, modo de visualização");
        }
    }, [isSystemType, navigate, toast])

    const disabledCart = useMemo(() => {
        const isPos = isSystemType(SystemType.pospayOrderPad);
        const isPrepay = isSystemType(SystemType.prepayTicket);
        return !isPos && !isPrepay;
        // if (!isPos && !isPrepay) {
        //     toast("Nenhuma operaçao detectada");
        //     navigate('/session');
        // }
    }, [isSystemType])

    const handleInvoiceConfig = useCallback((paymentType: PaymentType) => {
        if (paymentType === PaymentType.credito) {
            return deviceConfig?.invoice.manualEmitOnCreditEnabled ?? false;
        }
        if (paymentType === PaymentType.debito) {
            return deviceConfig?.invoice.manualEmitOnDebitEnabled  ?? false;
        }
        if (paymentType === PaymentType.dinheiro) {
            return deviceConfig?.invoice.manualEmitOnCashEnabled ?? false;
        }
        if (paymentType === PaymentType.pix) {
            return deviceConfig?.invoice.manualEmitOnPixEnabled ?? false;
        }
        if (paymentType === PaymentType.misto) {
            return deviceConfig?.invoice.manualEmitOnMixedEnabled ?? false;
        }
        return true;
    }, [deviceConfig]);

    const getExtractAndOrderPad = useCallback(
        async (tag: string) => {
            if (device) {
                try {
                    setOnLoadingExtract(true)
                    const orderPadResponse = await getOrderPad(tag, device.localId);
                    const extractResponse = await getExtract(orderPadResponse.id, device.localId);
                } catch (e) {
                    console.error(e)
                } finally {
                    setOnLoadingExtract(false)
                }
            }

        }, [device, getExtract, getOrderPad],
    )

    const getExtractByTable = useCallback(
        async (tableId: string) => {
            if (device) {
                try {
                    setOnLoadingExtract(true)
                    await getExtract(tableId, device.localId);
                } catch (e) {
                    console.error(e)
                } finally {
                    setOnLoadingExtract(false)
                }
            }

        }, [device, getExtract],
    )

    const getExtractByTagOrTable = useCallback(() => {
        if (tag) {
            getExtractAndOrderPad(tag);
        }
        if (tableId) {
            getExtractByTable(tableId)
        }

    }, [getExtractAndOrderPad, getExtractByTable, tableId, tag])

    useEffect(() => {
        getExtractByTagOrTable()
    }, [getExtractByTagOrTable])

    const onClickBackHandle = useCallback(
        () => {
            clearCart();
            if (operationMode === OperationMode.ORDER_PAD) {
                navigate('/orderPad');
            } else if (operationMode === OperationMode.TABLE) {
                navigate('/tableMap');
            } else if (operationMode === OperationMode.CASHLESS) {
                navigate('/session');
            } else if (operationMode === OperationMode.TICKET) {
                navigate('/session');
            }
        },
        [clearCart, navigate, operationMode],
    )


    const updateTutorialOnboarding = useCallback(async () => {
        const onBoardingService = OnBoardingService();
        await onFinishStep(onBoardingService, {
            localId: device?.localId ?? '',
            step: ticket ? OnBoardingSteps.OnSendOrderTicket : OnBoardingSteps.OnSendOrder,
            description: 'Ao enviar o pedido'
        })
    }, [device?.localId, onFinishStep, ticket])


    const finishPaymentRedirect = useCallback(async () => {
        clearCart();
        try {
            showLoading('@finishPaymentRedirect', t('loadingMessages.finalizing'));
            await updateTutorialOnboarding();
        } finally {
            hideLoading('@finishPaymentRedirect');
        }
        if (deviceConfig?.terminal.operatorMode === OperatorMode.ONE) {
            onClickBackHandle();
        } else {
            logoutSessionHandler();
        }
    }, [clearCart, deviceConfig?.terminal.operatorMode, logoutSessionHandler, onClickBackHandle, t, updateTutorialOnboarding])


    const onClickClosePaymentButtonHandle = useCallback(
        () => {
            setSearchParms(prev => queryParamsJSON(prev, { openCart: undefined, openPayAccount: undefined }))
            getExtractByTagOrTable();
        },
        [getExtractByTagOrTable, setSearchParms],
    )


    useEffect(() => {
        const keyDownHandler = event => {
            if (event.key === 'Escape') {
                // event.preventDefault();
                // onClickBackHandle();
            }
        };

        document.addEventListener('keydown', keyDownHandler);
        return () => {
            document.removeEventListener('keydown', keyDownHandler);
        };
    }, [onClickBackHandle]);





    const sendOrderPosPaySharedOrderPadHandler = useCallback(async () => {
        if (!extract) {
            getExtractByTagOrTable();
            toast(t('orderFeedback.errorSendingOrder'), 'error');
            return;
        }
        if (tableId && extract?.accountId && extract && currentOperator?.id) {
            const orderId: string = v4()
            const orderResponse = await consumeOrder(tableId, extract?.accountId, cartItems, number ?? "", orderId, currentOperator?.id)


            //printOrder(res.order.order, SystemType.pospayOrderPad);

            fullScreenToast(t('systemMessages.sentSuccessfully'), 'success', { sound: !disabledSound, action: finishPaymentRedirect })
            if (device && currentOperator) {
                printTicketOrder(orderResponse, SystemType.pospayOrderPad, device, currentOperator, number);
            }
            // onClickBackHandle();

            // const orderId: string = v4()
            // const order: IOrder = {
            //     id: orderId,
            //     orderItem: cartItems,
            // }
            // if (deviceConfig?.configSystem.find(item => item.systemType === SystemType.pospayOrderPad)?.enableRemotePrint) {
            //     sendOrderToLocalPrint(order, number ?? "");
            // } //TODO:IMPLEMENTAR REMOTE PRINT
        }
    }, [extract, tableId, currentOperator, getExtractByTagOrTable, toast, t, consumeOrder, cartItems, number, fullScreenToast, disabledSound, finishPaymentRedirect, device, printTicketOrder])

    const sendOrderPosPayIndividualOrderPadHandler = useCallback(
        async () => {
            if (!extract) {
                getExtractByTagOrTable();
                toast(t('orderFeedback.errorSendingOrder'), 'error');
                return;
            }
            if (orderPad && extract && currentOperator?.id && device) {
                await consumeOrder(orderPad?.id, orderPad?.accountId, cartItems, number ?? "", v4(), currentOperator?.id);

                fullScreenToast(t('orderFeedback.orderSentSuccessfully'), 'success', { sound: !disabledSound, action: onClickBackHandle });

                // const orderId: string = v4();
                // const order: IOrder = {
                //     id: orderId,
                //     orderItem: cartItems,
                // }
                // if (deviceConfig?.configSystem.find(item => item.systemType === SystemType.pospayOrderPad)?.enableRemotePrint) {
                //     sendOrderToLocalPrint(order, number ?? "");
                // }//TODO:IMPLEMENTAR REMOTE PRINT
            }
        },
        [extract, orderPad, currentOperator?.id, device, getExtractByTagOrTable, toast, t, consumeOrder, cartItems, number, fullScreenToast, disabledSound, onClickBackHandle],
    )

    const paymentwithCieloPos = useCallback(async (orderId: string, amount: number, simplePos?: ISimplePos | null) => {
        try {
            if (!device?.id)
                throw new Error('Dispositivo não encontrado')
            showLoading('@cieloPos', t('loadingMessages.payingWithCieloPos'));
            const response = await paymentCieloPos(device?.id, orderId, amount, simplePos?.posId);
            return response;
        } catch (e: any) {
            throw new Error(e.message ?? 'Erro ao pagar com CIELO');
        } finally {
            hideLoading('@cieloPos');
        }
    }, [device?.id, paymentCieloPos, t])

    const sendOrderTicketHandler = useCallback(async (transactions: SyncOrderTransactions[], customerValues?: ICustomerFormValue, simplePos?: ISimplePos, discount?: IDiscountValue, notes?: string) => {
        if (isSystemType(SystemType.prepayTicket) && device) {
            try {
                if (transactions.some(payment => payment.paymentType === PaymentType.cielo)) {

                    const cieloPayments = transactions.filter(payment => payment.paymentType === PaymentType.cielo);

                    for (const payment of cieloPayments) {
                        const cieloResponse = await paymentwithCieloPos(v4(), payment.amount ?? 0, simplePos);

                        payment.transactionData = JSON.stringify(cieloResponse);
                    }

                }
                showLoading('@sendOrderTicketHandler', t('loadingMessages.sendingOrder'));
                let panelCode = "";

                if (session?.enabledCodePanel) {
                    const panelCodeResponse = await GetCodePanelUseCase(codePanelService, device.localId, session?.id);
                    panelCode = panelCodeResponse?.passwordForOrder?.toString() ?? "";
                }
                const response = await consumeTicketOrder(cartItems, totals.totalPrice, transactions, customerValues, panelCode, discount, notes);

                fullScreenToast(t('orderFeedback.orderSentSuccessfullyExclamation'), 'success', { sound: !disabledSound, action: finishPaymentRedirect });

                if (device && currentOperator) {
                    printTicketOrder(response, SystemType.prepayTicket, device, currentOperator, undefined, panelCode);
                }

                const shouldBlockInvoice = transactions.some(payment => !handleInvoiceConfig(payment.paymentType));

                if (session?.emitInvoice && shouldBlockInvoice) {
                    emitBackgroundInvoice(response.id, '');
                }
            } catch (error: any) {
                hideLoading('@sendOrderTicketHandler');
                fullScreenToast(error?.message ?? t('orderFeedback.errorSendingOrder'), 'warning', { sound: !disabledSound, icon: 'error' });
            } finally {
                hideLoading('@sendOrderTicketHandler');
            }
        }
    }, [isSystemType, device, t, session, consumeTicketOrder, cartItems, totals.totalPrice, fullScreenToast, disabledSound, finishPaymentRedirect, currentOperator, handleInvoiceConfig, paymentwithCieloPos, printTicketOrder, emitBackgroundInvoice]);




    const onSubmitPayment = useCallback(async (paymentType: PaymentType, amount: number, payServiceFee?: boolean, simplePos?: ISimplePos | null, discount?: IDiscountValue) => {
        try {
            if (extract) {

                if (paymentType === PaymentType.cielo) {
                    await paymentwithCieloPos(v4(), amount, simplePos);
                }
                showLoading('@onClickPayAccountButtonHandle', t('loadingMessages.payingAccount'));


                const response = await payAccount(paymentType === PaymentType.cielo ? PaymentType.credito : paymentType, extract, amount, payServiceFee, discount);
                printPosPayment(extract, number ?? tag ?? tableId ?? '', amount);
                if (response.accountClosed) {
                    fullScreenToast(t('orderFeedback.paymentCompletedSuccessfully'), 'success', { sound: !disabledSound, action: finishPaymentRedirect });
                    if (session?.emitInvoice && !handleInvoiceConfig(paymentType)) { emitBackgroundInvoice(response.orderId, extract.user?.document ?? '') }
                    // onClickBackHandle();
                    // finishPaymentRedirect();
                } else {
                    onClickClosePaymentButtonHandle();
                    toast(t('orderFeedback.paymentCompletedSuccessfullySimple'), 'success');
                }
            }
        } catch (error: any) {
            // toast(error?.message ?? "falha ao pagar conta", 'error');
            fullScreenToast(error?.message ?? t('orderFeedback.accountPaymentFailure'), 'warning', { sound: !disabledSound, icon: 'error' });
        } finally {
            hideLoading('@onClickPayAccountButtonHandle');
        }

    }, [extract, t, payAccount, printPosPayment, number, tag, tableId, paymentwithCieloPos, fullScreenToast, disabledSound, finishPaymentRedirect, session?.emitInvoice, handleInvoiceConfig, emitBackgroundInvoice, onClickClosePaymentButtonHandle, toast])


    const closeAccountHandle = useCallback(async () => {
        try {
            showLoading('@onClickPayAccountButtonHandle', t('loadingMessages.closingAccount'));
            await closeAccount(extract);
            fullScreenToast(t('orderFeedback.accountClosedSuccessfully'), 'success', { sound: !disabledSound, action: finishPaymentRedirect });
            // onClickBackHandle();
            // finishPaymentRedirect()

        } catch (error: any) {
            toast(error.message ?? t('orderFeedback.errorClosingAccount'), 'error');
        } finally {
            hideLoading('@onClickPayAccountButtonHandle');
        }
    }, [closeAccount, disabledSound, extract, finishPaymentRedirect, fullScreenToast, t, toast]);


    const cancelAccoutOrderItemsHandler = useCallback(async (cancelOrderValue: ICancelOrderItemSelectable[], annotation: string) => {
        try {
            showLoading('@cancelOrderItems', t('loadingMessages.cancelingItems'));
            await cancelOrderItems(cancelOrderValue, annotation, extract);
            getExtractByTagOrTable();
            return
        } catch (error: any) {
            toast(error.message ?? t('orderFeedback.errorCancelingItems'), 'error');
            throw new Error(error.message ?? t('orderFeedback.errorCancelingItems'));
        } finally {
            hideLoading('@cancelOrderItems');
        }
    }, [cancelOrderItems, extract, getExtractByTagOrTable, t, toast])

    const transferOrderItemsHandler = useCallback(async (orderItems: ITransferItemSelectable[], toTag: string, annotation?: string) => {
        try {
            showLoading('@transferOrderItems', t('loadingMessages.transferringItems'));
            await transferOrderItems(orderItems, tableId ?? '', toTag, annotation, extract);
            getExtractByTagOrTable();
            return
        } catch (error: any) {
            toast(error.message ?? t('orderFeedback.errorTransferringItems'), 'error');
            throw new Error(error.message ?? t('orderFeedback.errorTransferringItems'));
        } finally {
            hideLoading('@transferOrderItems');
        }
    }, [extract, getExtractByTagOrTable, t, tableId, toast, transferOrderItems]);


    return ({
        extract,
        orderPad,
        tag,
        number,
        onLoadingExtract,
        onClickBackHandle,
        sendOrderPosPaySharedOrderPadHandler,
        sendOrderPosPayIndividualOrderPadHandler,
        sendOrderTicketHandler,
        onSubmitPayment,
        closeAccountHandle,
        getExtractAndOrderPad,
        cancelOrderItems,
        cancelAccoutOrderItemsHandler,
        transferOrderItemsHandler,
        disabledCart,
        printExtractHandler,
        receiptPrinterSector,
        tableId,
    })
}



export default UseCatalogPage